import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, withKeys as _withKeys, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["id", "data-epoch-id"]
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "epoch__moments" }

import { computed, nextTick, Ref, ref, toRef, onUnmounted, watch} from "vue";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";
import { useTimelineDom } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useTimelineDom";
import { useScrollPinning } from "@features/Pages/TimelineMapPage/_js/app/composables/mm/useScrollPinning";
import { readDocumentCssPx } from "@features/Pages/TimelineMapPage/_js/app/services/readDocumentCssPx";
import MomentMM from "../moment/MomentMM.vue";
import type { IEpoch } from "../../interfaces/IEpoch";


export default /*@__PURE__*/_defineComponent({
  __name: 'EpochMM',
  props: {
    epoch: {},
    index: {},
    activeIndex: {},
    timelineEl: {}
  },
  setup(__props: any) {

const props = __props;

const store = useTimelineMapStore();

const timelineRef = toRef(props, 'timelineEl') as unknown as Ref<HTMLElement | null>;
const { goToEpoch } = useTimelineDom(timelineRef);

let titleClickCooldown = false;
const { smoothScrollTo, readCssPx, stopPin } = useScrollPinning();

const renderedMomentIndexes = ref<number[]>([]);
const titleRef = ref<HTMLElement | null>(null);

const isActive = computed(() => store.activeEpochId === props.epoch.id);
const epochDistance = computed(() => props.activeIndex - props.index);

const visibleMoments = computed(() => {
  const items = props.epoch.timelineMapItems;
  const start = Math.max(0, props.epoch.startIndex ?? 0);
  const count = Math.min(items.length - start, props.epoch.visibleCount ?? items.length);
  return items.slice(start, start + count);
});

function fontStepForDistance(d: number, a: string, b: string, c: string) {
  // d = epochDistance from active epoch
  if (d === 0 || d === -1) { return a; }
  if (d === -2 || d === 1) { return b; }
  if (d === -3 || d === 2) { return c; }
  
  return "0px";
}

const computedFontSize = computed(() => fontStepForDistance(epochDistance.value, "26px", "22px", "16px"));
const computedLineHeight = computed(() => fontStepForDistance(epochDistance.value, "31px", "26px", "22px"));
const computedMarginTop = computed(() => (epochDistance.value === -1 ? "24px" : "0px"));

const computedColor = computed(() => { 
  const d = epochDistance.value;
  if (d === -1 || d === 1) { return "var(--color-bremon-100)"; }
  if (d === -2 || d === 2) { return "var(--color-storm-80)"; }
  if (d === -3) { return "var(--color-storm-100)"; }
  
  return "";
});

async function onTitleClick(e?: MouseEvent) {  
  if (titleClickCooldown) { return; }
  titleClickCooldown = true;
  setTimeout(() => (titleClickCooldown = false), 250);
  
  const titleElement = titleRef.value;
  if (!titleElement) { return; }

  if (store.isDragging || (e && e.button !== 0)) {
    e?.preventDefault();
    e?.stopImmediatePropagation?.();
    e?.stopPropagation();
    return;
  }

  const prevIdx = store.epochs.findIndex(e => e.id === store.activeEpochId);
  const nextIdx = store.epochs.findIndex(e => e.id === props.epoch.id);
  const movingDown = nextIdx > prevIdx;

  await goToEpoch(props.epoch.id);
  await nextTick();
  await new Promise(r => requestAnimationFrame(r));

  const margin = readCssPx(".site--mm", "--epoch-lock-top", 200);
  const disturbanceHeight = readDocumentCssPx("--disturbance-height", 0);
  const downBias = movingDown ? readCssPx(".site--mm", "--scroll-down-bias", 18) : 0;
  
  const rect = titleElement.getBoundingClientRect();
  const targetTop = window.scrollY + rect.top - margin - disturbanceHeight - downBias;
  await smoothScrollTo(targetTop, 700);
}

function onTitleKeydown(e: KeyboardEvent) {
  if (store.isDragging) { return; }
  
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    onTitleClick();
  }
}

function revealMomentsSequentially() {
  renderedMomentIndexes.value = [];
  nextTick(() => {
    for (let i = 0; i < visibleMoments.value.length; i++) {
      setTimeout(() => { renderedMomentIndexes.value.push(i); }, i * 100);
    }
  });
}

onUnmounted(() => { stopPin(); });

watch(isActive, (nowActive) => {
  if (nowActive) {
    revealMomentsSequentially();
    } else {
        renderedMomentIndexes.value = [];
    }
  }, { immediate: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    id: __props.epoch.epochType,
    "data-epoch-id": __props.epoch.id,
    class: "epoch"
  }, [
    _createElementVNode("h2", {
      class: _normalizeClass(["epoch__title", { active: isActive.value }]),
      style: _normalizeStyle({ fontSize: computedFontSize.value, lineHeight: computedLineHeight.value, marginTop: computedMarginTop.value, color: computedColor.value }),
      id: 'epoch-title-' + __props.epoch.id,
      ref_key: "titleRef",
      ref: titleRef,
      onClick: onTitleClick,
      onKeydown: [
        _withKeys(onTitleKeydown, ["space"]),
        _withKeys(onTitleKeydown, ["enter"])
      ],
      tabindex: "0"
    }, _toDisplayString(__props.epoch.epochYearName), 47 /* TEXT, CLASS, STYLE, PROPS, NEED_HYDRATION */, _hoisted_2),
    _createElementVNode("div", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMoments.value, (item, idx) => {
        return _withDirectives((_openBlock(), _createBlock(MomentMM, {
          key: item.urlSegment,
          moment: item,
          index: idx,
          animateIn: isActive.value && renderedMomentIndexes.value.includes(idx)
        }, null, 8 /* PROPS */, ["moment", "index", "animateIn"])), [
          [_vShow, renderedMomentIndexes.value.includes(idx)]
        ])
      }), 128 /* KEYED_FRAGMENT */))
    ])
  ], 8 /* PROPS */, _hoisted_1))
}
}

})